import { Controller } from "@hotwired/stimulus"

// Base class for Stimulus controllers that wrap a heavy, lazy-loaded third-party
// widget (Choices, Tagify, flatpickr, Pickr, CodeMirror 6, Toast UI) on a form
// field. It solves the two Turbo + Stimulus hazards that bit every widget after
// the jQuery → Stimulus migration:
//
// 1. RE-PARENT LOOP (memory balloon). Some widgets re-parent their own element
//    into a wrapper on init (Choices wraps the <select>, Tagify wraps the
//    <input>). That re-parent makes Stimulus fire disconnect → connect, and the
//    re-connect runs on a BRAND-NEW controller instance — so an instance-level
//    `if (this.widget) return` guard is undefined again and we re-initialise,
//    re-wrap, re-parent, and loop forever (main thread blocked, memory → GBs).
//    Fixed with a flag ON THE ELEMENT (survives the re-parent and new instances)
//    plus a disconnect() that ignores the re-parent (element still in document).
//
// 2. TURBO PAGE-CACHE DOUBLE-INIT (the "widget blinks on back/forward"). Turbo
//    snapshots the DOM for its cache. If we only tore down in disconnect() (which
//    runs AFTER the snapshot), the cached page keeps the widget-wrapped DOM and we
//    re-initialise on top of it on restore. Fixed by also tearing down on
//    turbo:before-cache, so the snapshot keeps the ORIGINAL element.
//
// Subclasses implement:
//   async load()      -> lazy import(s); returns whatever build() needs
//   build(loaded)     -> construct the widget (+ any DOM side-effects); return the
//                        instance, or a falsy value to decline (e.g. missing id)
//   destroy(widget)   -> optional; defaults to widget.destroy(). Override to also
//                        undo DOM side-effects (remove inserted nodes, unhide the
//                        original field, detach listeners).
//
// NEW WIDGET CONTROLLERS MUST EXTEND THIS CLASS — do not re-implement the
// lifecycle with a plain instance-only guard (see COMMON_PITFALLS.md).
export default class extends Controller {
  async connect() {
    // Re-parent guard: bail if this element already has a live widget. The flag
    // lives on the element, so it survives re-parenting and new instances.
    if (this.element.dataset[this.initFlag] === "true") return
    if (this.widget) return
    this._connected = true

    const loaded = await this.load()
    // Bail if Turbo disconnected us during the async import, or a re-parent
    // re-connect raced ahead and already initialised.
    if (!this._connected || this.widget || this.element.dataset[this.initFlag] === "true") return

    // Mark BEFORE build() so the disconnect/connect a re-parent triggers sees the
    // flag and bails instead of looping.
    this.element.dataset[this.initFlag] = "true"
    const widget = this.build(loaded)
    if (!widget) {
      // Subclass declined to initialise (e.g. element missing a required id).
      delete this.element.dataset[this.initFlag]
      return
    }
    this.widget = widget

    this._onBeforeCache = () => this.teardown()
    document.addEventListener("turbo:before-cache", this._onBeforeCache)
  }

  disconnect() {
    // A widget re-parenting the still-in-document element makes Stimulus report a
    // disconnect; ignore it. Only tear down when the element is really gone (a
    // real Turbo navigation away).
    if (this.element.isConnected) return
    this.teardown()
  }

  teardown() {
    this._connected = false
    if (this._onBeforeCache) {
      document.removeEventListener("turbo:before-cache", this._onBeforeCache)
      this._onBeforeCache = null
    }
    if (this.widget != null) {
      try {
        this.destroy(this.widget)
      } catch (e) {
        // Widget already torn down (e.g. its DOM was removed by Turbo) — ignore.
      }
      this.widget = null
    }
    delete this.element.dataset[this.initFlag]
  }

  // camelCase dataset key, namespaced by controller identifier so two widget
  // controllers on one element never collide
  // ("choices" -> "choicesWidgetInitialized" -> data-choices-widget-initialized).
  get initFlag() {
    if (!this._initFlag) {
      const camel = this.identifier.replace(/-([a-z])/g, (_, c) => c.toUpperCase())
      this._initFlag = `${camel}WidgetInitialized`
    }
    return this._initFlag
  }

  // --- subclass hooks ---
  async load() { return null }
  build(_loaded) { throw new Error(`${this.identifier}: build() not implemented`) }
  destroy(widget) { if (widget && typeof widget.destroy === "function") widget.destroy() }
}
